import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { faPlus } from '@fortawesome/pro-regular-svg-icons/faPlus';
import { useNavigate } from '@rocketlane/rocket-router';
import { Button, Icon } from '@rocketlane/ui';
import { useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { FormattedMessage } from 'react-intl';
import { PageLoading } from 'components/atoms/loading';
import { nitroBrewEditPath, nitroBrewRunPath } from 'components/organisms/nitro-claw/nitro-claw-desktop-sidebar.config';
import { NITRO_BREWS_MESH } from 'components/organisms/nitro-claw/nitro-claw-mesh.config';
import { ClawNitroBrewSections } from 'components/organisms/nitro-brew/claw-nitro-brew-sections';
import { useUser } from 'hooks/use-user';
import { useTranslate } from 'hooks/use-translation';
import { useNitroBrewAgentsQuery } from 'queries/nitro-brew';
import { MeshSphere } from 'vendor/meshy';
const CREATE_PATH = '/agent-builder/nitro-brew/create?nc=1';
export const ClawNitroBrewListPage = ()=>{
    const navigate = useNavigate();
    const translate = useTranslate();
    const { user } = useUser();
    const { data: agents = [], isLoading, isError, refetch } = useNitroBrewAgentsQuery();
    const title = translate('nitro_claw.sidebar.agents.nitro_brews', undefined, 'Nitro Brews');
    const handleNewBrew = useCallback(()=>{
        navigate(CREATE_PATH);
    }, [
        navigate
    ]);
    const handleOpen = useCallback((brew)=>{
        navigate(nitroBrewEditPath(brew.id));
    }, [
        navigate
    ]);
    const handleEdit = useCallback((brew)=>{
        navigate(`${nitroBrewEditPath(brew.id)}?mode=edit`);
    }, [
        navigate
    ]);
    const handleRun = useCallback((brew)=>{
        navigate(nitroBrewRunPath(brew.id));
    }, [
        navigate
    ]);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Helmet, {
                children: /*#__PURE__*/ _jsx("title", {
                    children: title
                })
            }),
            /*#__PURE__*/ _jsx("div", {
                "data-cy": "claw-nitro-brew-list-page",
                className: "cp-flex cp-h-full cp-min-h-0 cp-flex-1 cp-flex-col cp-overflow-y-auto cp-bg-[var(--white)] cp-px-[var(--spacing-05)] cp-pt-[var(--spacing-10)] cp-pb-[var(--spacing-05)]",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "cp-mx-auto cp-flex cp-w-full cp-max-w-[700px] cp-flex-col cp-items-center cp-gap-[var(--spacing-09)]",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "cp-flex cp-w-full cp-flex-col cp-items-center cp-gap-[var(--spacing-05)]",
                            children: [
                                /*#__PURE__*/ _jsx(MeshSphere, {
                                    colors: NITRO_BREWS_MESH.colors,
                                    meshId: NITRO_BREWS_MESH.meshId,
                                    size: 72,
                                    animated: true,
                                    "data-cy": "claw-nitro-brew-list.hero-mesh"
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "cp-flex cp-flex-col cp-items-center cp-gap-[var(--spacing-03)] cp-text-center",
                                    children: [
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "!cp-font-[var(--font-weight-bold)] heading-04 cp-text-[var(--text-secondary)]",
                                            children: /*#__PURE__*/ _jsx(FormattedMessage, {
                                                id: "nitro_claw.sidebar.agents.nitro_brews",
                                                defaultMessage: "Nitro Brews"
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "cp-m-0 cp-max-w-[420px] body-long-01 cp-text-[var(--text-secondary)]",
                                            children: /*#__PURE__*/ _jsx(FormattedMessage, {
                                                id: "nitro_claw.nitro_brews.description",
                                                defaultMessage: "Brews are your own agents. Every brew you build, and every one a teammate has shared with you, lives here."
                                            })
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(Button, {
                                    type: "button",
                                    variant: "primary",
                                    size: "medium",
                                    leftIcon: /*#__PURE__*/ _jsx(Icon, {
                                        icon: faPlus
                                    }),
                                    onClick: handleNewBrew,
                                    "data-cy": "claw-nitro-brew-list.new",
                                    children: /*#__PURE__*/ _jsx(FormattedMessage, {
                                        id: "nitro_claw.sidebar.agents.nitro_brews.new",
                                        defaultMessage: "New brew"
                                    })
                                })
                            ]
                        }),
                        isLoading ? /*#__PURE__*/ _jsx(PageLoading, {}) : isError ? /*#__PURE__*/ _jsxs("div", {
                            className: "cp-flex cp-w-full cp-flex-col cp-items-center cp-gap-[var(--spacing-04)] cp-py-[var(--spacing-08)]",
                            "data-cy": "claw-nitro-brew-list.error",
                            children: [
                                /*#__PURE__*/ _jsx("p", {
                                    className: "cp-m-0 body-long-01 cp-text-[var(--text-secondary)]",
                                    children: /*#__PURE__*/ _jsx(FormattedMessage, {
                                        id: "error.generic.description"
                                    })
                                }),
                                /*#__PURE__*/ _jsx(Button, {
                                    type: "button",
                                    variant: "secondary",
                                    size: "medium",
                                    onClick: ()=>void refetch(),
                                    "data-cy": "claw-nitro-brew-list.retry",
                                    children: /*#__PURE__*/ _jsx(FormattedMessage, {
                                        id: "retry"
                                    })
                                })
                            ]
                        }) : /*#__PURE__*/ _jsx(ClawNitroBrewSections, {
                            agents: agents,
                            currentUserId: user.userId,
                            onOpen: handleOpen,
                            onEdit: handleEdit,
                            onRun: handleRun
                        })
                    ]
                })
            })
        ]
    });
};
